/**
 * 分包记录面板组件
 */
import React, { useState, useEffect } from 'react';
import { Table, Spin, Empty, message, Tag } from 'antd';
import type { ColumnsType } from 'antd/es/table';
import api from '../../../services/api';
import { formatMoney } from '../../../utils/budgetUtils';

interface SubcontractPanelProps {
  projectId: string;
  isMobile?: boolean;
  active?: boolean;
}

interface SubcontractRecord {
  _id: string;
  costDate: string;
  contractName: string;
  contractAmount: number;
  paidAmount: number;
  status: 'active' | 'completed' | 'cancelled';
  description?: string;
}

interface SubcontractResponse {
  success: boolean;
  data?: SubcontractRecord[];
  pagination?: {
    total: number;
    page: number;
    limit: number;
    totalPages: number;
  };
}

const getStatusTag = (status: SubcontractRecord['status']) => {
  if (status === 'completed') return <Tag color="green">已完成</Tag>;
  if (status === 'cancelled') return <Tag color="red">已取消</Tag>;
  return <Tag color="blue">进行中</Tag>;
};

const SubcontractPanel: React.FC<SubcontractPanelProps> = ({
  projectId,
  isMobile = false,
  active = false
}) => {
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState<SubcontractRecord[]>([]);
  const [pagination, setPagination] = useState({
    current: 1,
    pageSize: isMobile ? 5 : 10,
    total: 0
  });
  const [loaded, setLoaded] = useState(false);

  const fetchData = async (page = 1, pageSize = isMobile ? 5 : 10) => {
    if (!projectId) return;

    setLoading(true);
    try {
      const response = await api.get('/subcontract-costs', {
        params: {
          projectId,
          page,
          limit: pageSize
        }
      }) as SubcontractResponse;

      if (response.success && response.data) {
        setData(response.data);
        setPagination({
          current: response.pagination?.page || page,
          pageSize: response.pagination?.limit || pageSize,
          total: response.pagination?.total || 0
        });
      }
    } catch (error) {
      console.error('获取分包记录失败:', error);
      message.error('获取分包记录失败');
    } finally {
      setLoading(false);
      setLoaded(true);
    }
  };

  useEffect(() => {
    if (active && !loaded) {
      fetchData();
    }
  }, [active, loaded, projectId]);

  const formatDate = (dateStr: string) => {
    if (!dateStr) return '-';
    return dateStr.split('T')[0];
  };

  const columns: ColumnsType<SubcontractRecord> = isMobile ? [
    {
      title: '合同/日期',
      key: 'info',
      render: (_, record) => (
        <div>
          <div style={{ fontWeight: 500 }}>{record.contractName}</div>
          <div style={{ fontSize: 12, color: '#666' }}>
            {formatDate(record.costDate)} · {getStatusTag(record.status)}
          </div>
          {record.description && (
            <div style={{ fontSize: 11, color: '#999', marginTop: 2 }}>
              {record.description}
            </div>
          )}
        </div>
      )
    },
    {
      title: '金额',
      key: 'amount',
      width: 100,
      align: 'right',
      render: (_, record) => {
        const amount = record.paidAmount > 0 ? record.paidAmount : record.contractAmount;
        return formatMoney(amount, false);
      }
    }
  ] : [
    {
      title: '日期',
      dataIndex: 'costDate',
      key: 'costDate',
      width: 120,
      render: (val: string) => formatDate(val)
    },
    {
      title: '合同名称',
      dataIndex: 'contractName',
      key: 'contractName',
      width: 180,
      ellipsis: true
    },
    {
      title: '合同状态',
      dataIndex: 'status',
      key: 'status',
      width: 120,
      render: (val: SubcontractRecord['status']) => getStatusTag(val)
    },
    {
      title: '金额',
      key: 'actualAmount',
      width: 130,
      align: 'right',
      render: (_, record) => {
        const amount = record.paidAmount > 0 ? record.paidAmount : record.contractAmount;
        return formatMoney(amount, false);
      }
    },
    {
      title: '备注',
      dataIndex: 'description',
      key: 'description',
      ellipsis: true,
      render: (val: string) => val || '-'
    }
  ];

  if (!loaded && !active) {
    return null;
  }

  if (loading && !data.length) {
    return (
      <div style={{ textAlign: 'center', padding: '20px 0' }}>
        <Spin tip="加载中..." />
      </div>
    );
  }

  if (!data.length) {
    return <Empty description="暂无分包记录" image={Empty.PRESENTED_IMAGE_SIMPLE} />;
  }

  return (
    <Table
      columns={columns}
      dataSource={data}
      rowKey="_id"
      loading={loading}
      size="small"
      pagination={{
        ...pagination,
        size: 'small',
        showSizeChanger: false,
        showTotal: (total) => `共 ${total} 条`,
        onChange: (page, pageSize) => fetchData(page, pageSize)
      }}
    />
  );
};

export default SubcontractPanel;
